import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import { CommonButton } from '../../../../Styles/CommonStyles';
import useAuth from '../../../../features/hooks/useAuth';
import { useSelector } from 'react-redux';
import { useGetUsersQuery } from '../../../../features/users/usersApiSlice';
import { useUpdateConsumableReqMutation } from '../../../../features/consumablerequest/consumableReqApiSlice';
import { useGetofficesQuery } from '../../../../features/offices/officesApiSlice';
import { useGetStoreOfficesQuery } from '../../../../features/storeoffices/storeofficesApiSlice';
import { useGetComplMastersQuery } from '../../../../features/complmaster/complmasterApiSlice';
import UnivConsumReqFlow from './UnivConsumReqFlow';

// Import role-specific form components
import UserInboxFormCIR from './UserInboxFormCIR';
import BoInboxFormCIR from './BoInboxFormCIR';
import UnitHeadInboxFormCIR from './UnitHeadInboxFormCIR';
import StoreL1InboxFormCIR from './StoreL1InboxFormCIR';
import StoreL2InboxFormCIR from './StoreL2InboxFormCIR';

// Styled Components
const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', sans-serif;
`;

const BackButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
`;

const BackButton = styled.div`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  color: #1976d2;

  &:hover {
    text-decoration: underline;
  }
`;

const Button = styled(CommonButton)`
  padding: 8px 16px;
  min-width: 120px;
  margin-right: 10px;
`;

const ActionButton = styled(Button)`
  background-color: #1976d2;
`;

const ActionContainer = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 15px;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  align-self: flex-start;
`;

const ModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxHeight: '80vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: '8px',
  overflow: 'auto'
};

const ModalTitle = styled.h2`
  margin-bottom: 20px;
  color: #1976d2;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  gap: 10px;
`;

// Add new styled components for the partial consumables modal
const PartialConsumablesModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '500px',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: '8px'
};

const OptionButton = styled(Button)`
  margin: 8px 0;
  width: 100%;
  padding: 12px;
  justify-content: flex-start;
  text-transform: none;
  font-weight: 500;
`;

const ForwardStoreButton = styled(OptionButton)`
  background-color: #2196f3;
`;

const CloseRequestButton = styled(OptionButton)`
  background-color: #f44336;
`;

// New styled components for the modify selection modal
const ModifySelectionModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxHeight: '80vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: '8px',
  overflow: 'auto'
};

const ConsumIssueDetails = ({ row, subTabValue, setChangepage, from }) => {
  const [showActionOptions, setShowActionOptions] = useState(false);
  const [openConsumableModal, setOpenConsumableModal] = useState(false);
  const [filterProps, setFilterProps] = useState({});
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [userRole, setUserRole] = useState("user");

  // New state to track whether options are loaded
  const [optionsLoaded, setOptionsLoaded] = useState(false);

  const auth = useAuth();
  let user_current_office = (useSelector((state) => state.office.off));

  // Get store offices data
  const { storeoffices } = useGetStoreOfficesQuery('storeoffices', {
    selectFromResult: ({ data }) => ({
      storeoffices: data?.entities
    }),
  });

  // Get consumable master data
  const { consumablemaster } = useGetComplMastersQuery('consumablemasters', {
    selectFromResult: ({ data }) => ({
      consumablemaster: data?.entities
    }),
  });

  const { getSingleuser } = useGetUsersQuery('userslist', {
    selectFromResult: ({ data }) => ({
      getSingleuser: data?.entities
    }),
  });

  const { officedata } = useGetofficesQuery('offices', {
    selectFromResult: ({ data }) => ({
      officedata: data?.entities
    }),
  });

  const BO_id = officedata ? Object.values(officedata)?.find(office => office?.name === user_current_office)?.Bo : "";
  const UnitHead_id = officedata ? Object.values(officedata)?.find(office => office?.name === user_current_office)?.unitHead : "";

  const [updateReq, { isLoading: updateLoading }] = useUpdateConsumableReqMutation();

  // Check if user is a BO (Branch Officer)
  const isBO = auth?.roles?.main?.includes('Bo') ||
    Object.values(auth?.roles || {}).some(roles =>
      Array.isArray(roles) && roles.includes('Bo'));

  // Check if user is a Unit Head
  const isUnitHead = auth?.roles?.main?.includes('UnitHead') ||
    Object.values(auth?.roles || {}).some(roles =>
      Array.isArray(roles) && roles.includes('UnitHead'));
 

  const handleBack = () => {
    setChangepage([true, false, false, false, false]);
  };

  const handleTakeAction = () => {
    setShowActionOptions(true);
  }; 

  const handleCloseModal = () => {
    setOpenConsumableModal(false);
    setSelectedOptions([]);
  };
  

  // Track when options are loaded
  useEffect(() => {
    if (storeoffices && consumablemaster) {
      setOptionsLoaded(true);
    }
  }, [storeoffices, consumablemaster]);
  

  useEffect(() => {
    if (isBO) {
      setFilterProps({ perm_cur_loc: ["office"], perm_alloc_ofc: [user_current_office] });
    }
  }, [isBO]);
  
  const columnFilter = ["Specification", "Manufacturer", "Purchase Date", "Warranty Date", "Detail"]

  // Check user roles
  useEffect(() => {
      if (auth) {
        const newRoles = ["user"]; // Start with a base role
  
        const isBO = auth?.roles?.main?.includes('Bo') ||
          Object.values(auth?.roles || {}).some(rolesArray =>
            Array.isArray(rolesArray) && rolesArray.includes('Bo'));
        
        const isUnitHead = auth?.roles?.main?.includes('UnitHead') ||
          Object.values(auth?.roles || {}).some(rolesArray =>
            Array.isArray(rolesArray) && rolesArray.includes('UnitHead'));
        
        const isStoreL1 = auth?.roles?.main?.includes('L1ReqApprover_C') ||
          Object.values(auth?.roles || {}).some(rolesArray =>
            Array.isArray(rolesArray) && rolesArray.includes('L1ReqApprover_C'));
        
        const isStoreL2 = auth?.roles?.main?.includes('L2ReqApprover_C') ||
          Object.values(auth?.roles || {}).some(rolesArray =>
            Array.isArray(rolesArray) && rolesArray.includes('L2ReqApprover_C'));
        
        if (isStoreL2) newRoles.push("L2ReqApprover_C");
        if (isStoreL1) newRoles.push("L1ReqApprover_C");
        if (isUnitHead) newRoles.push("UnitHead");
        if (isBO) newRoles.push("Bo");
        
        // To prevent unnecessary re-renders if roles haven't actually changed
        // This is a more robust way to avoid loops if `auth` itself is stable
        setUserRole(prevRoles => {
          const finalRoles = [...new Set(newRoles)]; // Ensure unique roles
          // Check if the new roles are different from the previous ones
          if (prevRoles.length === finalRoles.length && prevRoles.every((role, index) => role === finalRoles[index])) {
            return prevRoles; // Return the old state if no change
          }
          return finalRoles; // Return the new state
        });
      }
    }, [auth]);
    console.log(userRole);
    console.log(subTabValue)
  // Render the appropriate form based on user role and request status
  const renderRoleSpecificForm = () => {
    // If the request is not meant for this user, don't show action options
    if (!isRequestForUser()) {
      return null;
    }

    if (userRole.includes("user") && subTabValue === 0 ) {
      return <UserInboxFormCIR row={row} handleBack={handleBack} selectedOptions={selectedOptions} />;
    }
    if (userRole.includes("Bo") && subTabValue === 1) {
      return <BoInboxFormCIR row={row} handleBack={handleBack} selectedOptions={selectedOptions} setSelectedOptions={setSelectedOptions} />;
    }
    if (userRole.includes("UnitHead") && subTabValue === 2) {
      return <UnitHeadInboxFormCIR row={row} handleBack={handleBack} />;
    }
    if (userRole.includes("L1ReqApprover_C") && subTabValue === 3) {
      return <StoreL1InboxFormCIR row={row} handleBack={handleBack} />;
    }
    if (userRole.includes("L2ReqApprover_C") && subTabValue === 4) {
      return <StoreL2InboxFormCIR row={row} handleBack={handleBack} />;
    }
    return null;
  };

  // Helper function to check if this request is for the current user to action
  const isRequestForUser = () => {
    const lastAction = row?.req_data?.[row.req_data.length - 1];
    if (!lastAction) return false;
    
    return lastAction.sent_to?.id === auth?.id;
  };

  // Check if the request was closed or rejected
  const isRequestClosedOrRejected = 
    row?.curr_status === 'Closed' || 
    row?.curr_status === 'Rejected' ||
    row?.req_data?.[row.req_data.length - 1]?.sent_to_tag === 'boreq_rejct_closed' ||
    row?.req_data?.[row.req_data.length - 1]?.sent_to_tag === 'ureq_paraccept_close' ||
    row?.req_data?.[row.req_data.length - 1]?.sent_to_tag === 'ureq_allasstbolvl_close' ||
    row?.req_data?.[row.req_data.length - 1]?.sent_to_tag === 'lvl2req_rejection_appvd';

  console.log(isRequestForUser());
  console.log(from)

  return (
    <Container>
      <BackButtonWrapper>
        <BackButton onClick={handleBack}>
          <ArrowLeftIcon />
          <span style={{ marginLeft: '8px' }}>{`Back to ${from === "sentbox"? "SentBox" : "Inbox"}`}</span>
        </BackButton>
      </BackButtonWrapper>

      <UnivConsumReqFlow row={row} />
      

    <ActionContainer>
      {!showActionOptions ? (
        <ButtonsWrapper>
          {/* Don't show Take Action button if the request was rejected or closed,
              or if the request is not meant for this user */}
          {!isRequestClosedOrRejected && isRequestForUser() && (
            <Button onClick={handleTakeAction}>Take Action</Button>
          )}
        </ButtonsWrapper>
      ) : (
        <>
          {renderRoleSpecificForm()}
        </>
      )}
    </ActionContainer>

    </Container>
  );
};

export default ConsumIssueDetails;